import api from './client'
import type {
  InfrastructureStatus,
  GeminiUsage,
  AIMetrics,
  ActivityEvent,
} from '@/types/dashboard'

export interface DashboardStats {
  active_entities: number
  automations: number
  plugins_loaded: number
  tools_available: number
  uptime_seconds: number
}

export interface EntityState {
  entity_id: string
  name: string
  state: string
  icon: string | null
  last_changed: string | null
}

export interface RecentActivity {
  description: string
  timestamp: string
  entity_id: string | null
}

export interface DashboardResponse {
  ok: boolean
  stats: DashboardStats
  entities: EntityState[]
  recent_activity: RecentActivity[]
}

/**
 * Fetch aggregated dashboard data.
 */
export async function getDashboard(): Promise<DashboardResponse> {
  const response = await api.get<DashboardResponse>('/dashboard')
  return response.data
}

/**
 * Format uptime seconds to human readable string.
 */
export function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86400)}d`
}

// ============================================================
// Infrastructure Status APIs
// ============================================================

/**
 * Fetch comprehensive infrastructure status from all services.
 */
export async function getInfrastructureStatus(): Promise<InfrastructureStatus> {
  // Fetch data from multiple endpoints in parallel
  const [dashboardRes, healthRes, proxmoxRes, unifiRes] = await Promise.allSettled([
    api.get('/dashboard'),
    api.get('/health'),
    api.get('/proxmox/vms').catch(() => ({ data: { ok: false } })),
    api.get('/unifi/clients').catch(() => ({ data: { ok: false } })),
  ])

  // Extract dashboard data
  const dashboard =
    dashboardRes.status === 'fulfilled' ? dashboardRes.value.data : null
  const health = healthRes.status === 'fulfilled' ? healthRes.value.data : null
  const proxmox = proxmoxRes.status === 'fulfilled' ? proxmoxRes.value.data : null
  const unifi = unifiRes.status === 'fulfilled' ? unifiRes.value.data : null

  // Build Home Assistant status
  const haPlugin = health?.plugins?.home_assistant
  const homeAssistant = {
    connected: haPlugin?.ok ?? false,
    entityCount: dashboard?.stats?.active_entities ?? 0,
    activeEntities: dashboard?.entities?.filter(
      (e: EntityState) => e.state === 'on' || e.state === 'locked'
    ).length ?? 0,
    automationCount: dashboard?.stats?.automations ?? 0,
    version: haPlugin?.version,
  }

  // Build Proxmox status
  const proxmoxPlugin = health?.plugins?.proxmox
  const proxmoxStatus = {
    connected: proxmoxPlugin?.ok ?? false,
    nodeHealth: proxmox?.node_health ?? null,
    vms: (proxmox?.vms ?? []).filter((v: { type: string }) => v.type === 'qemu'),
    containers: (proxmox?.vms ?? []).filter((v: { type: string }) => v.type === 'lxc'),
  }

  // Build UniFi status
  const unifiPlugin = health?.plugins?.unifi
  const unifiStatus = {
    connected: unifiPlugin?.ok ?? false,
    clientCount: unifi?.total ?? 0,
    wiredCount: unifi?.wired ?? 0,
    wirelessCount: unifi?.wireless ?? 0,
    alertCount: unifi?.alerts ?? 0,
  }

  // Build plugin health status
  const pluginNames = Object.keys(health?.plugins ?? {})
  const healthyPlugins = pluginNames.filter(
    (name) => health?.plugins?.[name]?.ok
  )
  const degradedPlugins = pluginNames.filter(
    (name) => !health?.plugins?.[name]?.ok
  )

  return {
    homeAssistant,
    proxmox: proxmoxStatus,
    unifi: unifiStatus,
    plugins: {
      healthy: healthyPlugins.length,
      total: pluginNames.length,
      degraded: degradedPlugins,
    },
  }
}

// ============================================================
// AI Metrics APIs
// ============================================================

/**
 * Fetch Gemini token usage statistics.
 */
export async function getGeminiUsage(): Promise<GeminiUsage> {
  try {
    const response = await api.get<{
      ok: boolean
      usage: {
        tokens_today: number
        tokens_this_month: number
        cost_today_usd: number
        cost_this_month_usd: number
        model_breakdown: Array<{
          model: string
          input_tokens: number
          output_tokens: number
          cost_usd: number
        }>
        daily_usage: Array<{
          date: string
          total_tokens: number
        }>
      }
    }>('/gemini/usage')

    if (response.data.ok && response.data.usage) {
      const u = response.data.usage
      // Calculate week total from daily_usage (last 7 days)
      const weekTokens = u.daily_usage
        ?.slice(0, 7)
        .reduce((sum, d) => sum + (d.total_tokens || 0), 0) || 0

      return {
        tokensToday: u.tokens_today || 0,
        tokensThisWeek: weekTokens,
        tokensThisMonth: u.tokens_this_month || 0,
        estimatedCostUsd: u.cost_this_month_usd || 0,
        modelBreakdown: (u.model_breakdown || []).map((m) => ({
          model: m.model,
          inputTokens: m.input_tokens || 0,
          outputTokens: m.output_tokens || 0,
        })),
        dailyUsage: (u.daily_usage || []).map((d) => ({
          date: d.date,
          tokens: d.total_tokens || 0,
        })),
      }
    }
  } catch {
    // Endpoint may not exist yet, return defaults
  }

  return {
    tokensToday: 0,
    tokensThisWeek: 0,
    tokensThisMonth: 0,
    estimatedCostUsd: 0,
    modelBreakdown: [],
    dailyUsage: [],
  }
}

/**
 * Fetch comprehensive AI metrics including feedback and errors.
 */
export async function getAIMetrics(): Promise<AIMetrics> {
  const [feedbackRes, errorsRes, geminiRes] = await Promise.allSettled([
    api.get('/feedback/metrics'),
    api.get('/errors/patterns'),
    getGeminiUsage(),
  ])

  const feedback =
    feedbackRes.status === 'fulfilled' ? feedbackRes.value.data : null
  const errors =
    errorsRes.status === 'fulfilled' ? errorsRes.value.data : null
  const geminiUsage =
    geminiRes.status === 'fulfilled' ? geminiRes.value : null

  // Calculate satisfaction from feedback
  const total = (feedback?.metrics?.positive ?? 0) + (feedback?.metrics?.negative ?? 0)
  const overall = total > 0 ? (feedback?.metrics?.positive ?? 0) / total : 0

  return {
    geminiUsage: geminiUsage ?? {
      tokensToday: 0,
      tokensThisWeek: 0,
      tokensThisMonth: 0,
      estimatedCostUsd: 0,
      modelBreakdown: [],
      dailyUsage: [],
    },
    toolUsage: {
      totalCalls: 0, // TODO: Get from audit_log
      successRate: 0,
      topTools: feedback?.metrics?.tools_with_feedback?.map((name: string) => ({
        name,
        count: 0,
        satisfaction: null,
      })) ?? [],
      callsPerHour: [],
    },
    satisfaction: {
      overall,
      positive: feedback?.metrics?.positive ?? 0,
      negative: feedback?.metrics?.negative ?? 0,
      neutral: feedback?.metrics?.neutral ?? 0,
      lowPerformingTools: feedback?.metrics?.low_satisfaction_tools?.map(
        (t: { tool_name: string }) => t.tool_name
      ) ?? [],
    },
    errors: {
      totalPatterns: errors?.count ?? 0,
      newToday: errors?.patterns?.filter(
        (p: { status: string }) => p.status === 'new'
      ).length ?? 0,
      topPatterns: errors?.patterns?.slice(0, 5).map((p: {
        id: number
        signature: string
        tool_name: string
        error_sample: string
        occurrence_count: number
        first_seen: string
        last_seen: string
        status: string
      }) => ({
        id: p.id,
        signature: p.signature,
        toolName: p.tool_name,
        errorSample: p.error_sample,
        occurrenceCount: p.occurrence_count,
        firstSeen: p.first_seen,
        lastSeen: p.last_seen,
        status: p.status as 'new' | 'issue_created' | 'resolved',
      })) ?? [],
    },
  }
}

/**
 * Fetch activity events (for non-SSE fallback).
 */
export async function getActivityEvents(
  limit = 50,
  cursor?: string
): Promise<{ events: ActivityEvent[]; cursor?: string }> {
  try {
    const params = new URLSearchParams({ limit: String(limit) })
    if (cursor) params.set('cursor', cursor)

    const response = await api.get<{
      ok: boolean
      events: ActivityEvent[]
      cursor?: string
    }>(`/activity?${params}`)

    if (response.data.ok) {
      return {
        events: response.data.events,
        cursor: response.data.cursor,
      }
    }
  } catch {
    // Endpoint may not exist yet
  }

  return { events: [] }
}
